// ─── Pestaña Seguimiento del Cockpit del Contratista ───────────────────────
// Registros mensuales/semanales que el contratista rellena durante la obra
// para alimentar los criterios BREEAM de operaciones (CNT 2, RSD 1, MAT 3,
// SyB 2, GST 3/4, TRA…). Sub-flujos actuales:
//   · Consumos (energía, gasoil, agua, gas)  · CNT 2       · operativo
//   · Materiales entrantes                    · MAT 3, SyB 2 · próximamente
//   · Residuos                                · RSD 1       · próximamente
//   · Formaciones                             · GST 2       · próximamente
//   · Actas de obra                           · GST 3, GST 4 · próximamente
//   · Transportes                             · CNT 2, TRA  · próximamente

import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Lightning, Drop, Flame, GasPump, Plus, TrashSimple, DownloadSimple,
  FilePdf, Spinner, X, CheckCircle,
} from '@phosphor-icons/react'
import { supabase } from '../../lib/supabase'
import {
  uploadTempInvoices, analyzeInvoices, removeTempInvoices, archiveInvoices,
  buildInvoicePrompt,
} from '../../lib/invoiceImport'

// Mapeos kind (IA) → tipo/unidad del schema cc_seguimiento_consumos
const KIND_TO_TIPO   = { electricity: 'electrica', water: 'agua', gas: 'gas' }
const KIND_TO_UNIDAD = { electricity: 'kWh',       water: 'm³',   gas: 'kWh' }

const C = {
  text: '#2C2017', muted: '#7A6A5A', border: '#DDD5C8',
  bg: '#FFFFFF', bgAlt: '#FAF7F3', accent: '#0E2C4B',
  olive: '#8D9A2E', amber: '#C98A2E', coral: '#D4614A', dark: '#3B2F2F',
}
const FF = "'DM Sans', sans-serif"

const TIPOS_CONSUMO = [
  { code: 'electrica', label: 'Energía eléctrica', unidad: 'kWh', icon: Lightning, color: '#C98A2E' },
  { code: 'gasoil',    label: 'Gasoil / combustibles', unidad: 'L', icon: GasPump, color: '#8D6B3E' },
  { code: 'agua',      label: 'Agua', unidad: 'm³', icon: Drop, color: '#3E6B9E' },
  { code: 'gas',       label: 'Gas natural', unidad: 'kWh', icon: Flame, color: '#D4614A' },
]
const TIPO_MAP = Object.fromEntries(TIPOS_CONSUMO.map(t => [t.code, t]))

const SUB_TABS = [
  { id: 'consumos',    label: 'Consumos',  enabled: true },
  { id: 'materiales',  label: 'Materiales',  enabled: false },
  { id: 'residuos',    label: 'Residuos',    enabled: false },
  { id: 'formaciones', label: 'Formaciones', enabled: false },
  { id: 'actas',       label: 'Actas obra',  enabled: false },
  { id: 'transportes', label: 'Transportes', enabled: false },
]

export default function Seguimiento({ expedienteId, projectId, userId, isAdmin }) {
  const [subTab, setSubTab] = useState('consumos')

  if (!expedienteId) {
    return (
      <div style={{
        padding: '2rem', border: `1px dashed ${C.border}`, borderRadius: 6,
        color: C.muted, fontFamily: FF, textAlign: 'center', fontSize: '0.88rem',
      }}>
        Configura primero el expediente en la pestaña Setup para empezar a registrar seguimientos.
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Sub-tabs de flujos */}
      <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
        {SUB_TABS.map(t => {
          const active = t.id === subTab
          return (
            <button
              key={t.id}
              onClick={() => t.enabled && setSubTab(t.id)}
              disabled={!t.enabled}
              title={t.enabled ? '' : 'Próximamente'}
              style={{
                padding: '0.4rem 0.8rem', borderRadius: 4,
                border: `1px solid ${active ? C.dark : C.border}`,
                background: active ? C.dark : (t.enabled ? 'white' : '#F5F0EA'),
                color: active ? 'white' : (t.enabled ? C.text : C.muted),
                fontFamily: FF, fontSize: '0.8rem', fontWeight: active ? 600 : 500,
                cursor: t.enabled ? 'pointer' : 'not-allowed',
                opacity: t.enabled ? 1 : 0.55,
              }}>
              {t.label}
              {!t.enabled && (
                <span style={{ fontSize: '0.68rem', marginLeft: '0.35rem', color: C.muted }}>
                  · próximamente
                </span>
              )}
            </button>
          )
        })}
      </div>

      {subTab === 'consumos' && (
        <SeguimientoConsumos
          expedienteId={expedienteId}
          projectId={projectId}
          userId={userId}
          isAdmin={isAdmin}
        />
      )}
    </div>
  )
}

// ─── Sub-flujo · Consumos ──────────────────────────────────────────────────
function SeguimientoConsumos({ expedienteId, projectId, userId, isAdmin }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState(null)
  const [pdfImport, setPdfImport] = useState(null)     // { processing, invoices[], error, fileInfos[] }
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState(null)
  const [splitByMonth, setSplitByMonth] = useState(true)
  const [filterTipo, setFilterTipo] = useState('all')
  const [filterAnio, setFilterAnio] = useState('all')
  const [sortBy, setSortBy] = useState('mes')           // 'mes' | 'tipo' | 'cantidad' | 'coste'
  const [sortDir, setSortDir] = useState('desc')

  const cargar = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    const { data, error } = await supabase
      .from('cc_seguimiento_consumos')
      .select('*')
      .eq('project_id', projectId)
      .order('mes', { ascending: false })
      .order('tipo', { ascending: true })
    if (error) setError(error.message)
    setRows(data || [])
    setLoading(false)
  }, [projectId])

  useEffect(() => { cargar() }, [cargar])

  const eliminar = async (row) => {
    if (!window.confirm(`¿Eliminar el consumo de ${TIPO_MAP[row.tipo]?.label} de ${row.mes.slice(0, 7)}?`)) return
    if (row.factura_storage_path) {
      await supabase.storage.from('cc-documentos-proyecto').remove([row.factura_storage_path])
    }
    const { error } = await supabase.from('cc_seguimiento_consumos').delete().eq('id', row.id)
    if (error) { alert(`Error · ${error.message}`); return }
    setRows(rs => rs.filter(x => x.id !== row.id))
  }

  const descargarFactura = async (path) => {
    if (!path) return
    const { data } = await supabase.storage.from('cc-documentos-proyecto').createSignedUrl(path, 3600)
    if (data?.signedUrl) window.open(data.signedUrl, '_blank')
  }

  // ── Bulk import de facturas (PDF/imagen) mediante IA ────────────────────
  const handleImportPDFs = async (files) => {
    if (!files?.length) return
    if (!projectId) { setError('Sin proyecto activo'); return }
    setImportResult(null)
    setPdfImport({ processing: true, invoices: [], error: null, fileNames: Array.from(files).map(f => f.name) })
    try {
      const { file_infos, skipped } = await uploadTempInvoices(files, userId, projectId)
      if (!file_infos.length) throw new Error('No se pudo procesar ningún archivo:\n• ' + skipped.join('\n• '))
      if (skipped.length) console.warn('PDFs descartados:', skipped)
      const invoices = await analyzeInvoices(file_infos, buildInvoicePrompt({ splitByMonth }))
      if (!invoices.length) throw new Error('La IA no detectó facturas legibles en los archivos.')
      // Editable en el panel: añadir index para poder editar/borrar filas
      const editable = invoices.map((inv, idx) => ({ idx, ...inv }))
      setPdfImport({ processing: false, invoices: editable, error: null, fileInfos: file_infos, fileNames: file_infos.map(f => f.name) })
    } catch (e) {
      setPdfImport({ processing: false, invoices: [], error: e.message, fileNames: [] })
    }
  }

  const cancelPDFImport = async () => {
    await removeTempInvoices(pdfImport?.fileInfos || [])
    setPdfImport(null)
  }

  const updateInvoice = (idx, patch) => {
    setPdfImport(pi => pi ? { ...pi, invoices: pi.invoices.map(i => i.idx === idx ? { ...i, ...patch } : i) } : pi)
  }
  const removeInvoice = (idx) => {
    setPdfImport(pi => pi ? { ...pi, invoices: pi.invoices.filter(i => i.idx !== idx) } : pi)
  }

  const confirmPDFImport = async () => {
    if (!pdfImport?.invoices?.length) return
    console.log('[confirmPDFImport] arranca · expedienteId:', expedienteId, 'projectId:', projectId, 'userId:', userId, 'invoices:', pdfImport.invoices.length)
    if (!expedienteId || !projectId || !userId) {
      setPdfImport(pi => pi ? { ...pi, error: `Faltan datos de contexto: expediente=${expedienteId} · project=${projectId} · user=${userId}` } : pi)
      return
    }
    setImporting(true); setError(null)
    try {
      // 1. Archivar los PDFs a cc-documentos-proyecto/{user}/{project}/facturas/
      console.log('[confirmPDFImport] archivando facturas…', pdfImport.fileInfos)
      const archivedMap = await archiveInvoices(pdfImport.fileInfos, userId, projectId, 'cc-documentos-proyecto')
      console.log('[confirmPDFImport] archived map:', [...archivedMap.entries()])
      // 2. Construir 1 payload por factura
      const payloads = pdfImport.invoices.map(inv => {
        const tipo   = KIND_TO_TIPO[inv.kind]   || 'electrica'
        const unidad = KIND_TO_UNIDAD[inv.kind] || 'kWh'
        const facturaPath = archivedMap.get(inv.source_filename) || null
        // La cantidad puede ser negativa (regularizaciones, abonos, autoconsumo
        // FV con excedente). El schema lo admite (constraint retirada 2026-09-29).
        const cantidad = Number(inv.consumption)
        const coste = inv.amount_eur != null && inv.amount_eur !== '' ? Number(inv.amount_eur) : null
        const notasBits = [
          inv.supplier ? `Suministrador: ${inv.supplier}` : '',
          inv.notes || '',
        ].filter(Boolean)
        return {
          expediente_id: expedienteId,
          project_id: projectId,
          user_id: userId,
          mes: `${inv.year}-${String(inv.month).padStart(2, '0')}-01`,
          tipo,
          subtipo: null,
          cantidad,
          unidad,
          coste_eur: coste,
          factura_storage_path: facturaPath,
          factura_filename: inv.source_filename || null,
          notas: notasBits.join(' · ') || null,
        }
      })
      console.log('[confirmPDFImport] payloads a insertar:', payloads)
      // 3. Insertar uno a uno con manejo de duplicados (el UNIQUE es funcional
      //    sobre COALESCE(subtipo,'') → upsert de Supabase no lo entiende).
      let inserted = 0, duplicates = 0
      const insertErrors = []
      for (const p of payloads) {
        const { error } = await supabase.from('cc_seguimiento_consumos').insert(p)
        if (error) {
          console.warn('[confirmPDFImport] insert error:', error, 'payload:', p)
          if (error.code === '23505' || /duplicate/i.test(error.message)) { duplicates++; continue }
          insertErrors.push(`${p.mes.slice(0,7)} ${p.tipo}: ${error.message}`)
          continue
        }
        inserted++
      }
      if (insertErrors.length && inserted === 0) {
        throw new Error('Ningún registro se pudo insertar:\n• ' + insertErrors.join('\n• '))
      }
      setImportResult({
        ok: true, inserted, duplicates,
        errors: insertErrors.length ? insertErrors : null,
      })
      setPdfImport(null)
      cargar()
    } catch (e) {
      console.error('[confirmPDFImport] ERROR:', e)
      setPdfImport(pi => pi ? { ...pi, error: e.message } : pi)
      setImportResult({ ok: false, message: e.message })
    } finally {
      setImporting(false)
    }
  }

  // KPIs · último mes con datos por tipo, delta vs. mes anterior y total acumulado.
  // No usamos el mes calendario actual porque el contratista suele registrar
  // meses pasados en bloque · si nada este mes, todos los KPIs saldrían en blanco.
  const kpis = useMemo(() => {
    const acum = {}
    for (const t of TIPOS_CONSUMO) acum[t.code] = { actual: 0, anterior: 0, total: 0, mesActual: null, mesAnterior: null }
    // Agrupa filas por tipo y mes (suma subtipos del mismo mes)
    const porTipoYMes = {}
    for (const r of rows) {
      if (!acum[r.tipo]) continue
      const mesRow = r.mes.slice(0, 7)
      if (!porTipoYMes[r.tipo]) porTipoYMes[r.tipo] = new Map()
      porTipoYMes[r.tipo].set(mesRow, (porTipoYMes[r.tipo].get(mesRow) || 0) + Number(r.cantidad))
      acum[r.tipo].total += Number(r.cantidad)
    }
    // Para cada tipo, encuentra el mes más reciente con datos y el anterior
    for (const t of TIPOS_CONSUMO) {
      const m = porTipoYMes[t.code]
      if (!m || m.size === 0) continue
      const mesesOrd = [...m.keys()].sort().reverse()
      const mesUlt = mesesOrd[0]
      acum[t.code].actual = m.get(mesUlt)
      acum[t.code].mesActual = mesUlt
      if (mesesOrd[1]) {
        acum[t.code].anterior = m.get(mesesOrd[1])
        acum[t.code].mesAnterior = mesesOrd[1]
      }
    }
    return acum
  }, [rows])

  const aniosDisponibles = useMemo(() => {
    const set = new Set()
    for (const r of rows) set.add(r.mes.slice(0, 4))
    return [...set].sort().reverse()
  }, [rows])

  const visibleRows = useMemo(() => {
    let out = rows
    if (filterTipo !== 'all') out = out.filter(r => r.tipo === filterTipo)
    if (filterAnio !== 'all') out = out.filter(r => r.mes.slice(0, 4) === filterAnio)
    const dir = sortDir === 'asc' ? 1 : -1
    const cmp = (a, b) => {
      let va, vb
      if (sortBy === 'mes')       { va = a.mes;                vb = b.mes }
      else if (sortBy === 'tipo') { va = a.tipo;               vb = b.tipo }
      else if (sortBy === 'coste'){ va = a.coste_eur ?? 0;     vb = b.coste_eur ?? 0 }
      else                         { va = Number(a.cantidad);   vb = Number(b.cantidad) }
      if (va < vb) return -1 * dir
      if (va > vb) return  1 * dir
      // Desempate por mes desc para que dentro del mismo grupo el orden sea estable
      return a.mes < b.mes ? 1 : -1
    }
    return [...out].sort(cmp)
  }, [rows, filterTipo, filterAnio, sortBy, sortDir])

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortBy(col); setSortDir(col === 'mes' ? 'desc' : 'asc') }
  }
  const sortIndicator = (col) => sortBy === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* KPIs por tipo */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.6rem' }}>
        {TIPOS_CONSUMO.map(t => {
          const k = kpis[t.code]
          const hayDatos = k.mesActual !== null
          const delta = (hayDatos && k.anterior !== 0) ? ((k.actual - k.anterior) / Math.abs(k.anterior)) * 100 : null
          const Icon = t.icon
          const fmt = (n) => Number(n).toLocaleString('es-ES', { maximumFractionDigits: 2 })
          return (
            <div key={t.code} style={{
              padding: '0.75rem 0.9rem', border: `1px solid ${C.border}`,
              borderLeft: `3px solid ${t.color}`, borderRadius: 4, background: C.bgAlt, fontFamily: FF,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.3rem' }}>
                <Icon size={14} color={t.color} weight="regular" />
                <span style={{ fontSize: '0.7rem', color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                  {t.label}
                </span>
              </div>
              <div style={{ fontSize: '1.3rem', fontWeight: 700, color: hayDatos ? C.text : C.muted, lineHeight: 1.1 }}>
                {hayDatos ? fmt(k.total) : '—'}
                <span style={{ fontSize: '0.72rem', color: C.muted, marginLeft: '0.25rem', fontWeight: 500 }}>{t.unidad}</span>
              </div>
              <div style={{ fontSize: '0.7rem', color: C.muted, marginTop: '0.2rem' }}>
                {hayDatos ? 'total acumulado' : 'sin datos'}
              </div>
              {hayDatos && (
                <div style={{ fontSize: '0.68rem', color: C.muted, marginTop: '0.25rem', paddingTop: '0.25rem', borderTop: `1px dashed ${C.border}` }}>
                  Último mes ({k.mesActual}): <span style={{ color: C.text, fontWeight: 600 }}>{fmt(k.actual)} {t.unidad}</span>
                  {delta !== null && (
                    <span style={{ color: delta > 10 ? C.coral : (delta < -10 ? C.olive : C.muted), marginLeft: '0.35rem', fontWeight: 600 }}>
                      {delta > 0 ? '+' : ''}{delta.toFixed(0)}%
                    </span>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Tabla + botón añadir */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        <div style={{ fontSize: '0.88rem', fontWeight: 600, color: C.text, fontFamily: FF, flex: 1, display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
          <span>Histórico de consumos ({visibleRows.length}{visibleRows.length !== rows.length ? ` / ${rows.length}` : ''})</span>
          <select value={filterTipo} onChange={e => setFilterTipo(e.target.value)}
                  style={{ padding: '0.3rem 0.5rem', border: `1px solid ${C.border}`, borderRadius: 3, fontSize: '0.75rem', fontFamily: FF, background: 'white', color: C.text }}>
            <option value="all">Todos los tipos</option>
            {TIPOS_CONSUMO.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
          </select>
          {aniosDisponibles.length > 1 && (
            <select value={filterAnio} onChange={e => setFilterAnio(e.target.value)}
                    style={{ padding: '0.3rem 0.5rem', border: `1px solid ${C.border}`, borderRadius: 3, fontSize: '0.75rem', fontFamily: FF, background: 'white', color: C.text }}>
              <option value="all">Todos los años</option>
              {aniosDisponibles.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          )}
          {(filterTipo !== 'all' || filterAnio !== 'all') && (
            <button onClick={() => { setFilterTipo('all'); setFilterAnio('all') }}
                    style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: '0.72rem', fontFamily: FF, textDecoration: 'underline' }}>
              limpiar filtros
            </button>
          )}
        </div>
        <label title="Si se activa, una factura trimestral/anual se reparte en N lecturas mensuales prorrateadas. Desactívalo para factura única (por ejemplo agua anual)."
               style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                  fontFamily: FF, fontSize: '0.75rem', color: C.muted, cursor: 'pointer',
                }}>
          <input type="checkbox" checked={splitByMonth} onChange={e => setSplitByMonth(e.target.checked)} />
          Fraccionar por mes
        </label>
        <label style={{
                  padding: '0.5rem 0.9rem', background: 'white', color: C.text,
                  border: `1px solid ${C.border}`, borderRadius: 4, cursor: 'pointer',
                  fontFamily: FF, fontSize: '0.8rem', fontWeight: 600,
                  display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                }}>
          <FilePdf size={13} /> Importar facturas
          <input type="file" accept=".pdf,image/*" multiple style={{ display: 'none' }}
                 onChange={e => { const fs = Array.from(e.target.files || []); if (fs.length) handleImportPDFs(fs); e.target.value = '' }} />
        </label>
        <button onClick={() => { setEditing(null); setShowForm(true) }}
                style={{
                  padding: '0.5rem 0.9rem', background: C.dark, color: 'white',
                  border: 'none', borderRadius: 4, cursor: 'pointer',
                  fontFamily: FF, fontSize: '0.8rem', fontWeight: 600,
                  display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                }}>
          <Plus size={13} /> Añadir consumo
        </button>
      </div>

      {importResult && (
        <div style={{
          background: importResult.ok ? '#E8F5E9' : '#FFF1ED',
          border: `1px solid ${importResult.ok ? C.olive : C.coral}`,
          color: importResult.ok ? '#2E7D32' : C.coral,
          borderRadius: 4, padding: '0.55rem 0.8rem', fontSize: '0.82rem', fontFamily: FF,
          display: 'flex', alignItems: 'center', gap: '0.5rem',
        }}>
          {importResult.ok
            ? <><CheckCircle size={16} weight="fill" /> {importResult.inserted} consumos importados{importResult.duplicates > 0 && ` · ${importResult.duplicates} duplicados omitidos (ya existían para ese mes/tipo)`}</>
            : <>Error al importar: {importResult.message}</>}
          <button onClick={() => setImportResult(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>
            <X size={13} />
          </button>
        </div>
      )}

      {error && (
        <div style={{ background: '#FFF1ED', border: `1px solid ${C.coral}`, borderRadius: 4, padding: '0.5rem 0.8rem', color: C.coral, fontSize: '0.8rem', fontFamily: FF }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '1rem', color: C.muted, fontSize: '0.82rem', fontFamily: FF, fontStyle: 'italic' }}>
          Cargando…
        </div>
      ) : rows.length === 0 ? (
        <div style={{ padding: '1.5rem', border: `1px dashed ${C.border}`, borderRadius: 4, textAlign: 'center', color: C.muted, fontFamily: FF, fontSize: '0.85rem' }}>
          Aún no hay consumos registrados. Añade el primer mes con el botón de arriba.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: `1px solid ${C.border}`, borderRadius: 4 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', fontFamily: FF }}>
            <thead>
              <tr style={{ textAlign: 'left', background: C.bgAlt, color: C.muted, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                <th style={{ padding: '0.5rem 0.6rem', cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('mes')}>Mes{sortIndicator('mes')}</th>
                <th style={{ padding: '0.5rem 0.6rem', cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('tipo')}>Tipo{sortIndicator('tipo')}</th>
                <th style={{ padding: '0.5rem 0.6rem' }}>Subtipo</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right', cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('cantidad')}>Cantidad{sortIndicator('cantidad')}</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right', cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('coste')}>Coste €{sortIndicator('coste')}</th>
                <th style={{ padding: '0.5rem 0.6rem' }}>Factura</th>
                <th style={{ padding: '0.5rem 0.6rem' }}>Notas</th>
                <th style={{ padding: '0.5rem 0.6rem' }}></th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(r => {
                const t = TIPO_MAP[r.tipo]
                const Icon = t?.icon
                return (
                  <tr key={r.id} style={{ borderTop: `1px solid ${C.border}` }}>
                    <td style={{ padding: '0.45rem 0.6rem', color: C.text, whiteSpace: 'nowrap' }}>
                      {r.mes.slice(0, 7)}
                    </td>
                    <td style={{ padding: '0.45rem 0.6rem', color: C.text }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                        {Icon && <Icon size={13} color={t.color} />}
                        {t?.label || r.tipo}
                      </span>
                    </td>
                    <td style={{ padding: '0.45rem 0.6rem', color: C.muted, fontSize: '0.76rem' }}>
                      {r.subtipo || '—'}
                    </td>
                    <td style={{ padding: '0.45rem 0.6rem', textAlign: 'right', fontFamily: 'monospace' }}>
                      {Number(r.cantidad).toLocaleString('es-ES')} <span style={{ color: C.muted, fontSize: '0.72rem' }}>{r.unidad}</span>
                    </td>
                    <td style={{ padding: '0.45rem 0.6rem', textAlign: 'right', fontFamily: 'monospace', color: C.muted }}>
                      {r.coste_eur ? Number(r.coste_eur).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                    </td>
                    <td style={{ padding: '0.45rem 0.6rem' }}>
                      {r.factura_storage_path ? (
                        <button onClick={() => descargarFactura(r.factura_storage_path)}
                                title={r.factura_filename || 'Factura'}
                                style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem', fontFamily: FF }}>
                          <DownloadSimple size={11} /> Ver
                        </button>
                      ) : <span style={{ color: C.muted }}>—</span>}
                    </td>
                    <td style={{ padding: '0.45rem 0.6rem', color: C.muted, fontSize: '0.75rem', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.notas || ''}>
                      {r.notas || '—'}
                    </td>
                    <td style={{ padding: '0.45rem 0.6rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button onClick={() => { setEditing(r); setShowForm(true) }}
                              style={{ background: 'none', border: `1px solid ${C.border}`, color: C.text, cursor: 'pointer', fontSize: '0.72rem', padding: '0.2rem 0.5rem', borderRadius: 3, fontFamily: FF, marginRight: 4 }}>
                        Editar
                      </button>
                      <button onClick={() => eliminar(r)}
                              style={{ background: 'none', border: `1px solid ${C.coral}`, color: C.coral, cursor: 'pointer', fontSize: '0.72rem', padding: '0.2rem 0.5rem', borderRadius: 3, fontFamily: FF, display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                        <TrashSimple size={11} />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <FormConsumo
          expedienteId={expedienteId}
          projectId={projectId}
          userId={userId}
          row={editing}
          onClose={(saved) => {
            setShowForm(false)
            setEditing(null)
            if (saved) cargar()
          }}
        />
      )}

      {pdfImport && (
        <PanelImportFacturas
          pdfImport={pdfImport}
          importing={importing}
          onUpdate={updateInvoice}
          onRemove={removeInvoice}
          onCancel={cancelPDFImport}
          onConfirm={confirmPDFImport}
        />
      )}
    </div>
  )
}

// ─── Panel modal de confirmación de facturas importadas ─────────────────────
function PanelImportFacturas({ pdfImport, importing, onUpdate, onRemove, onCancel, onConfirm }) {
  const { processing, invoices = [], error, fileNames = [] } = pdfImport
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(59,47,47,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
    }}>
      <div style={{ background: 'white', borderRadius: 8, padding: '1.5rem', width: '95%', maxWidth: 1100, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
          <FilePdf size={22} color={C.dark} />
          <h3 style={{ margin: 0, fontFamily: FF, color: C.text, fontSize: '1rem', flex: 1 }}>
            Importar facturas ({fileNames.length} archivo{fileNames.length !== 1 ? 's' : ''})
          </h3>
          <button onClick={onCancel} disabled={importing}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ fontSize: '0.75rem', color: C.muted, marginBottom: '0.8rem', fontFamily: FF }}>
          {fileNames.map(n => <span key={n} style={{ display: 'inline-block', margin: '0 0.35rem 0.25rem 0', padding: '0.15rem 0.4rem', border: `1px solid ${C.border}`, borderRadius: 3, background: C.bgAlt }}>{n}</span>)}
        </div>

        {processing && (
          <div style={{ padding: '2rem', textAlign: 'center', color: C.muted, fontFamily: FF, fontSize: '0.88rem' }}>
            <Spinner size={24} weight="bold" className="spin" />
            <div style={{ marginTop: '0.75rem' }}>Analizando facturas con IA · esto puede tardar 20-40s…</div>
          </div>
        )}

        {error && (
          <div style={{ background: '#FFF1ED', border: `1px solid ${C.coral}`, borderRadius: 4, padding: '0.75rem 1rem', color: C.coral, fontSize: '0.82rem', fontFamily: FF, whiteSpace: 'pre-line' }}>
            {error}
          </div>
        )}

        {!processing && !error && invoices.length > 0 && (
          <>
            <div style={{ fontSize: '0.8rem', color: C.muted, fontFamily: FF, marginBottom: '0.5rem' }}>
              Se han detectado <strong style={{ color: C.text }}>{invoices.length}</strong> lecturas mensuales. Revisa y edita antes de confirmar.
              Las facturas trimestrales se muestran ya prorrateadas.
            </div>
            <div style={{ overflowX: 'auto', border: `1px solid ${C.border}`, borderRadius: 4 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem', fontFamily: FF }}>
                <thead>
                  <tr style={{ textAlign: 'left', background: C.bgAlt, color: C.muted, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    <th style={{ padding: '0.4rem 0.5rem' }}>Año</th>
                    <th style={{ padding: '0.4rem 0.5rem' }}>Mes</th>
                    <th style={{ padding: '0.4rem 0.5rem' }}>Tipo</th>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Consumo</th>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Coste €</th>
                    <th style={{ padding: '0.4rem 0.5rem' }}>Suministrador</th>
                    <th style={{ padding: '0.4rem 0.5rem' }}>Archivo origen</th>
                    <th style={{ padding: '0.4rem 0.5rem' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(inv => {
                    const unidad = KIND_TO_UNIDAD[inv.kind] || '?'
                    return (
                      <tr key={inv.idx} style={{ borderTop: `1px solid ${C.border}` }}>
                        <td style={{ padding: '0.3rem 0.5rem' }}>
                          <input type="number" value={inv.year} onChange={e => onUpdate(inv.idx, { year: parseInt(e.target.value) || inv.year })}
                                 style={miniInput(70)} />
                        </td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>
                          <input type="number" min="1" max="12" value={inv.month} onChange={e => onUpdate(inv.idx, { month: parseInt(e.target.value) || inv.month })}
                                 style={miniInput(50)} />
                        </td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>
                          <select value={inv.kind} onChange={e => onUpdate(inv.idx, { kind: e.target.value })} style={miniInput(110)}>
                            <option value="electricity">Eléctrica</option>
                            <option value="water">Agua</option>
                            <option value="gas">Gas</option>
                          </select>
                        </td>
                        <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right', fontFamily: 'monospace' }}>
                          <input type="number" step="0.01" value={inv.consumption} onChange={e => onUpdate(inv.idx, { consumption: e.target.value })}
                                 style={{ ...miniInput(85), textAlign: 'right' }} />
                          <span style={{ color: C.muted, marginLeft: 4, fontSize: '0.7rem' }}>{unidad}</span>
                        </td>
                        <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right', fontFamily: 'monospace' }}>
                          <input type="number" step="0.01" value={inv.amount_eur ?? ''} onChange={e => onUpdate(inv.idx, { amount_eur: e.target.value === '' ? null : e.target.value })}
                                 style={{ ...miniInput(80), textAlign: 'right' }} />
                        </td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>
                          <input type="text" value={inv.supplier || ''} onChange={e => onUpdate(inv.idx, { supplier: e.target.value })}
                                 style={miniInput(110)} />
                        </td>
                        <td style={{ padding: '0.3rem 0.5rem', color: C.muted, fontSize: '0.7rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={inv.source_filename}>
                          {inv.source_filename}
                        </td>
                        <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>
                          <button onClick={() => onRemove(inv.idx)} title="Descartar esta lectura"
                                  style={{ background: 'none', border: 'none', color: C.coral, cursor: 'pointer' }}>
                            <TrashSimple size={13} />
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button onClick={onCancel} disabled={importing || processing}
                  style={{ padding: '0.5rem 0.9rem', background: 'white', color: C.text, border: `1px solid ${C.border}`, borderRadius: 4, cursor: 'pointer', fontFamily: FF, fontSize: '0.82rem', fontWeight: 500 }}>
            Cancelar
          </button>
          {!processing && !error && invoices.length > 0 && (
            <button onClick={onConfirm} disabled={importing}
                    style={{ padding: '0.5rem 0.9rem', background: C.dark, color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontFamily: FF, fontSize: '0.82rem', fontWeight: 600, opacity: importing ? 0.6 : 1 }}>
              {importing ? 'Importando…' : `Confirmar e importar ${invoices.length} lecturas`}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function miniInput(width) {
  return {
    width, padding: '0.3rem 0.4rem', border: `1px solid ${C.border}`,
    borderRadius: 3, fontSize: '0.76rem', fontFamily: FF, boxSizing: 'border-box',
    background: 'white', color: C.text,
  }
}

// ─── Modal · alta/edición de un consumo ────────────────────────────────────
function FormConsumo({ expedienteId, projectId, userId, row, onClose }) {
  // Mes por defecto: mes actual (primer día).
  const hoy = new Date()
  const mesDefault = row?.mes?.slice(0, 7) ||
    `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`

  const [tipo, setTipo] = useState(row?.tipo || 'electrica')
  const [subtipo, setSubtipo] = useState(row?.subtipo || '')
  const [mes, setMes] = useState(mesDefault)
  const [cantidad, setCantidad] = useState(row?.cantidad ?? '')
  const [unidad, setUnidad] = useState(row?.unidad || TIPO_MAP[row?.tipo || 'electrica']?.unidad || 'kWh')
  const [coste, setCoste] = useState(row?.coste_eur ?? '')
  const [notas, setNotas] = useState(row?.notas || '')
  const [file, setFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)

  const cambiarTipo = (v) => {
    setTipo(v)
    // Si el usuario no está editando y no ha tocado la unidad, actualízala por defecto
    if (!row && TIPO_MAP[v]) setUnidad(TIPO_MAP[v].unidad)
  }

  const guardar = async () => {
    setError(null)
    if (cantidad === '' || cantidad == null || !isFinite(Number(cantidad))) { setError('Introduce una cantidad válida (puede ser negativa para abonos).'); return }
    if (!mes || !/^\d{4}-\d{2}$/.test(mes)) { setError('Selecciona un mes válido.'); return }

    setUploading(true)
    let facturaPath = row?.factura_storage_path || null
    let facturaName = row?.factura_filename || null

    try {
      // Subir factura si hay
      if (file) {
        const ext = file.name.split('.').pop() || 'pdf'
        const uid = crypto.randomUUID()
        const path = `${userId}/${projectId}/facturas/${uid}.${ext}`
        const { error: upErr } = await supabase.storage
          .from('cc-documentos-proyecto')
          .upload(path, file, { contentType: file.type || 'application/octet-stream', upsert: false })
        if (upErr) throw new Error(`Error subiendo factura: ${upErr.message}`)
        facturaPath = path
        facturaName = file.name
      }

      const payload = {
        expediente_id: expedienteId,
        project_id: projectId,
        user_id: userId,
        mes: `${mes}-01`,
        tipo,
        subtipo: subtipo.trim() || null,
        cantidad: Number(cantidad),
        unidad,
        coste_eur: coste === '' ? null : Number(coste),
        factura_storage_path: facturaPath,
        factura_filename: facturaName,
        notas: notas.trim() || null,
      }

      if (row?.id) {
        const { error } = await supabase.from('cc_seguimiento_consumos').update(payload).eq('id', row.id)
        if (error) throw new Error(error.message)
      } else {
        const { error } = await supabase.from('cc_seguimiento_consumos').insert(payload)
        if (error) {
          if (error.message?.includes('duplicate')) {
            throw new Error('Ya existe un registro para ese mes + tipo + subtipo. Edítalo en vez de crear uno nuevo.')
          }
          throw new Error(error.message)
        }
      }
      onClose(true)
    } catch (e) {
      setError(e.message)
    } finally {
      setUploading(false)
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(59,47,47,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(false) }}>
      <div style={{ background: 'white', borderRadius: 8, padding: '1.5rem', width: '90%', maxWidth: 520 }}>
        <h3 style={{ margin: '0 0 1rem 0', fontFamily: FF, color: C.text, fontSize: '1rem' }}>
          {row?.id ? 'Editar consumo' : 'Nuevo consumo'}
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <Field label="Tipo" span={2}>
            <select value={tipo} onChange={e => cambiarTipo(e.target.value)} style={inputStyle}>
              {TIPOS_CONSUMO.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Mes">
            <input type="month" value={mes} onChange={e => setMes(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Subtipo (opcional)">
            <input type="text" value={subtipo} onChange={e => setSubtipo(e.target.value)}
                   placeholder="Ej. red, grupo electrógeno" style={inputStyle} />
          </Field>
          <Field label="Cantidad *">
            <input type="number" step="0.01" value={cantidad}
                   onChange={e => setCantidad(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Unidad">
            <input type="text" value={unidad} onChange={e => setUnidad(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Coste (€) opcional" span={2}>
            <input type="number" step="0.01" value={coste}
                   onChange={e => setCoste(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Factura (PDF/imagen) opcional" span={2}>
            <input type="file" accept=".pdf,image/*"
                   onChange={e => setFile(e.target.files?.[0] || null)}
                   style={{ ...inputStyle, padding: '0.35rem', fontFamily: FF }} />
            {row?.factura_filename && !file && (
              <div style={{ fontSize: '0.72rem', color: C.muted, marginTop: '0.25rem' }}>
                Ya adjunta: {row.factura_filename}. Sube otra para reemplazar.
              </div>
            )}
          </Field>
          <Field label="Notas" span={2}>
            <textarea value={notas} onChange={e => setNotas(e.target.value)} rows={2}
                      style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }} />
          </Field>
        </div>

        {error && (
          <div style={{ marginTop: '0.75rem', padding: '0.55rem 0.8rem', background: '#FFF1ED', color: C.coral, border: `1px solid ${C.coral}`, borderRadius: 4, fontSize: '0.8rem', fontFamily: FF }}>
            {error}
          </div>
        )}

        <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button onClick={() => onClose(false)} disabled={uploading}
                  style={{ padding: '0.5rem 0.9rem', background: 'white', color: C.text, border: `1px solid ${C.border}`, borderRadius: 4, cursor: 'pointer', fontFamily: FF, fontSize: '0.82rem', fontWeight: 500 }}>
            Cancelar
          </button>
          <button onClick={guardar} disabled={uploading}
                  style={{ padding: '0.5rem 0.9rem', background: C.dark, color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontFamily: FF, fontSize: '0.82rem', fontWeight: 600, opacity: uploading ? 0.6 : 1 }}>
            {uploading ? 'Guardando…' : (row?.id ? 'Guardar cambios' : 'Guardar')}
          </button>
        </div>
      </div>
    </div>
  )
}

const inputStyle = {
  width: '100%', padding: '0.5rem 0.65rem', border: `1px solid ${C.border}`,
  borderRadius: 4, fontSize: '0.82rem', fontFamily: FF, boxSizing: 'border-box',
  color: C.text, background: 'white',
}

function Field({ label, span, children }) {
  return (
    <div style={{ gridColumn: span === 2 ? '1 / -1' : 'auto' }}>
      <label style={{ display: 'block', fontSize: '0.7rem', color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.3rem', fontFamily: FF }}>
        {label}
      </label>
      {children}
    </div>
  )
}
